import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setStoredToken, clearStoredToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = checking
  const [ready, setReady] = useState(false);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/team/me");
      setUser(data);
    } catch (e1) {
      // Fallback to legacy /auth/me
      try {
        const { data } = await api.get("/auth/me");
        if (data && data.role === "user") data.role = "admin";
        setUser(data);
      } catch (e2) {
        const s = e1?.response?.status === 401 || e1?.response?.status === 403
          ? e1.response.status : e2?.response?.status;
        if (s === 401 || s === 403) {
          setUser(false); // sessione davvero non valida
        } else {
          // Errore del SERVER o di rete (5xx/520/timeout): NON buttare fuori una
          // sessione valida né nascondere le voci admin. Mantieni lo stato e riprova.
          setUser((prev) => (prev ? prev : false));
          setTimeout(() => { refreshMe().catch(() => {}); }, 8000);
        }
      }
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  // Refresh user info quando un componente segnala un update del profilo
  useEffect(() => {
    const onUserUpdated = () => { refreshMe().catch(() => {}); };
    window.addEventListener("ba-user-updated", onUserUpdated);
    return () => window.removeEventListener("ba-user-updated", onUserUpdated);
  }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    const attempt = async () => {
      const { data } = await api.post("/auth/login", { email, password }, { timeout: 15000 });
      return data;
    };
    let data = null;
    let lastErr = null;
    // Una sola retry automatica su errore di rete (i.e. e.response undefined)
    for (let i = 0; i < 2; i++) {
      try {
        data = await attempt();
        break;
      } catch (e) {
        lastErr = e;
        if (e?.response) break; // errore HTTP — niente retry
        // Rete: aspetta 800ms e ritenta una volta
        await new Promise((r) => setTimeout(r, 800));
      }
    }
    if (data) {
      try {
        if (data?.access_token) setStoredToken(data.access_token);
        const userPart = {
          id: data.id, email: data.email, name: data.name,
          role: data.role === "user" ? "admin" : data.role,
          created_at: data.created_at,
        };
        setUser(userPart);
        setReady(true);
        refreshMe().catch(() => {});
        return { ok: true };
      } catch (e) { lastErr = e; }
    }
    // Errore: distingui network / auth / server
    const e = lastErr;
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    let msg;
    if (!e?.response) {
      msg = "Connessione non riuscita. Verifica la rete o tocca 'Diagnostica' qui sotto.";
    } else if (status === 401 || status === 403) {
      msg = formatApiError(detail) || "Credenziali non valide.";
    } else if (status === 429) {
      msg = formatApiError(detail) || "Troppi tentativi. Riprova tra qualche minuto.";
    } else if (status >= 500) {
      msg = "Errore del server. Riprova tra poco.";
    } else {
      msg = formatApiError(detail) || `Errore ${status || ""}`.trim() || "Impossibile accedere.";
    }
    return { ok: false, error: msg, status };
  }, [refreshMe]);

  const register = useCallback(async (email, password, name, inviteToken) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, invite_token: inviteToken });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearStoredToken();
    setUser(false);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, logout, refreshMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
